export interface CSSVarType {
  [key: string]: string;
}

// ---------------------------------------------------------------------------
// GUI Kit — Color Palette
//
// Every token stores a full rgb() value so IDE color-preview extensions work.
//
// Usage:
//   color: var(--ap-primary-500);
//   background: color-mix(in srgb, var(--ap-primary-500) 8%, transparent);
// ---------------------------------------------------------------------------

export const lightTheme: CSSVarType = {
  // =========================================================================
  // PRIMARY (teal) — 50 to 950
  // =========================================================================
  '--ap-primary-50': 'rgb(237, 247, 245)',
  '--ap-primary-100': 'rgb(217, 242, 238)',
  '--ap-primary-200': 'rgb(156, 211, 204)',
  '--ap-primary-300': 'rgb(113, 178, 169)',
  '--ap-primary-400': 'rgb(81, 157, 147)',
  '--ap-primary-500': 'rgb(50, 136, 125)',
  '--ap-primary-600': 'rgb(40, 109, 100)',
  '--ap-primary-700': 'rgb(36, 84, 78)',
  '--ap-primary-800': 'rgb(26, 63, 58)',
  '--ap-primary-900': 'rgb(18, 46, 42)',
  '--ap-primary-950': 'rgb(10, 30, 27)',

  // =========================================================================
  // SECONDARY (orange) — 50 to 950
  // =========================================================================
  '--ap-secondary-50': 'rgb(255, 247, 237)',
  '--ap-secondary-100': 'rgb(255, 237, 211)',
  '--ap-secondary-200': 'rgb(254, 215, 170)',
  '--ap-secondary-300': 'rgb(253, 186, 116)',
  '--ap-secondary-400': 'rgb(251, 146, 60)',
  '--ap-secondary-500': 'rgb(255, 128, 64)',
  '--ap-secondary-600': 'rgb(255, 106, 46)',
  '--ap-secondary-700': 'rgb(217, 119, 6)',
  '--ap-secondary-800': 'rgb(154, 52, 18)',
  '--ap-secondary-900': 'rgb(124, 45, 18)',
  '--ap-secondary-950': 'rgb(69, 20, 7)',

  // =========================================================================
  // DANGER (red) — 50 to 950
  // =========================================================================
  '--ap-danger-50': 'rgb(254, 242, 242)',
  '--ap-danger-100': 'rgb(254, 226, 226)',
  '--ap-danger-200': 'rgb(252, 202, 202)',
  '--ap-danger-300': 'rgb(252, 165, 165)',
  '--ap-danger-400': 'rgb(248, 113, 113)',
  '--ap-danger-500': 'rgb(217, 83, 79)',
  '--ap-danger-600': 'rgb(220, 38, 38)',
  '--ap-danger-700': 'rgb(185, 28, 28)',
  '--ap-danger-800': 'rgb(153, 27, 27)',
  '--ap-danger-900': 'rgb(127, 29, 29)',
  '--ap-danger-950': 'rgb(69, 10, 10)',

  // =========================================================================
  // NEUTRAL (gray) — 0 to 950
  // =========================================================================
  '--ap-neutral-0': 'rgb(255, 255, 255)',
  '--ap-neutral-50': 'rgb(242, 242, 242)',
  '--ap-neutral-100': 'rgb(229, 231, 235)',
  '--ap-neutral-200': 'rgb(220, 220, 220)',
  '--ap-neutral-300': 'rgb(192, 192, 192)',
  '--ap-neutral-400': 'rgb(158, 158, 158)',
  '--ap-neutral-500': 'rgb(128, 128, 128)',
  '--ap-neutral-600': 'rgb(100, 116, 139)',
  '--ap-neutral-700': 'rgb(51, 65, 85)',
  '--ap-neutral-800': 'rgb(31, 41, 55)',
  '--ap-neutral-900': 'rgb(30, 41, 59)',
  '--ap-neutral-950': 'rgb(0, 0, 0)',

  // =========================================================================
  // SEMANTIC ALIASES — cross-cutting tokens used app-wide
  // =========================================================================

  // TO DO: As conversion progress we might get rid of some of these tokens ( example: button-tokens)
  // Text
  '--text-primary': 'var(--ap-neutral-700)',
  '--text-secondary': 'var(--ap-neutral-800)',
  '--text-muted': 'var(--ap-neutral-600)',

  // Surfaces
  '--page-background': 'var(--ap-neutral-50)',
  '--form-background': 'var(--ap-neutral-0)',
  '--modal-background': 'var(--ap-neutral-0)',

  // States
  '--disabled': 'var(--ap-neutral-200)',
  '--link-color': 'var(--ap-primary-500)',
  '--error-danger': 'var(--ap-danger-500)',

  // Interaction states (widely used across components)
  '--ap-selection-bg':
    'color-mix(in srgb, var(--ap-primary-500) 8%, transparent)',
  '--ap-selection-border':
    'color-mix(in srgb, var(--ap-primary-500) 28%, transparent)',
  '--ap-focus-ring':
    'color-mix(in srgb, var(--ap-primary-500) 55%, transparent)',

  // Panels
  '--ap-panel-bg': 'color-mix(in srgb, var(--ap-neutral-0) 92%, transparent)',

  // Gradients
  '--ap-gradient-brand':
    'linear-gradient(135deg, var(--ap-primary-500) 0%, var(--ap-primary-700) 100%)',
  '--ap-gradient-secondary':
    'linear-gradient(135deg, var(--ap-secondary-500) 0%, var(--ap-secondary-600) 100%)',
};
